import { cn } from './cn';
import { Field } from './Field';

/**
 * A phone number with a country-code picker in front of it (owner's review,
 * 14 Oct 2026). The value is one string, `+<code> <number>` — `+91 9876543210`
 * — which is what the API stores for a lab or hospital. A bare ten-digit
 * number (everything saved before this) is read as Indian.
 */
export const DIAL_CODES: ReadonlyArray<{ code: string; country: string }> = [
  { code: '91', country: 'India' },
  { code: '971', country: 'United Arab Emirates' },
  { code: '977', country: 'Nepal' },
  { code: '880', country: 'Bangladesh' },
  { code: '94', country: 'Sri Lanka' },
  { code: '975', country: 'Bhutan' },
  { code: '960', country: 'Maldives' },
  { code: '92', country: 'Pakistan' },
  { code: '93', country: 'Afghanistan' },
  { code: '95', country: 'Myanmar' },
  { code: '966', country: 'Saudi Arabia' },
  { code: '974', country: 'Qatar' },
  { code: '965', country: 'Kuwait' },
  { code: '968', country: 'Oman' },
  { code: '973', country: 'Bahrain' },
  { code: '65', country: 'Singapore' },
  { code: '60', country: 'Malaysia' },
  { code: '66', country: 'Thailand' },
  { code: '62', country: 'Indonesia' },
  { code: '63', country: 'Philippines' },
  { code: '84', country: 'Vietnam' },
  { code: '86', country: 'China' },
  { code: '81', country: 'Japan' },
  { code: '82', country: 'South Korea' },
  { code: '61', country: 'Australia' },
  { code: '64', country: 'New Zealand' },
  { code: '44', country: 'United Kingdom' },
  { code: '353', country: 'Ireland' },
  { code: '49', country: 'Germany' },
  { code: '33', country: 'France' },
  { code: '39', country: 'Italy' },
  { code: '34', country: 'Spain' },
  { code: '31', country: 'Netherlands' },
  { code: '41', country: 'Switzerland' },
  { code: '46', country: 'Sweden' },
  { code: '7', country: 'Russia / Kazakhstan' },
  { code: '90', country: 'Türkiye' },
  { code: '20', country: 'Egypt' },
  { code: '27', country: 'South Africa' },
  { code: '254', country: 'Kenya' },
  { code: '234', country: 'Nigeria' },
  { code: '1', country: 'USA / Canada' },
  { code: '52', country: 'Mexico' },
  { code: '55', country: 'Brazil' },
];

/** Splits a stored value into its code and number; a bare number is Indian. */
export function splitPhone(value: string | null | undefined): { code: string; number: string } {
  const v = (value ?? '').trim();
  const m = /^\+(\d{1,4})\s*(\d*)$/.exec(v.replace(/[\s()-]+/g, (x) => (x.includes(' ') ? ' ' : '')));
  if (m) return { code: m[1]!, number: m[2]! };
  return { code: '91', number: v.replace(/\D/g, '') };
}

/** The stored form, or '' when no number was typed. */
export function joinPhone(code: string, number: string): string {
  const digits = number.replace(/\D/g, '');
  return digits ? `+${code} ${digits}` : '';
}

/** A problem with the number as typed, or null. India: 10 digits starting 6–9. */
export function phoneInputProblem(value: string): string | null {
  const { code, number } = splitPhone(value);
  if (!number) return null;
  if (code === '91') return /^[6-9]\d{9}$/.test(number) ? null : 'An Indian mobile number is 10 digits starting 6–9';
  return number.length >= 4 && number.length <= 14 ? null : 'Enter a valid number for that country';
}

export function PhoneInput({
  label, value, onChange, error, required, hint, disabled, placeholder,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  error?: string;
  required?: boolean;
  hint?: string;
  disabled?: boolean;
  placeholder?: string;
}) {
  const { code, number } = splitPhone(value);
  return (
    <Field label={label} error={error} required={required} hint={hint}>
      {(p) => (
        <div className="flex gap-2">
          <select
            aria-label={`${label} — country code`}
            value={code}
            disabled={disabled}
            onChange={(e) => onChange(joinPhone(e.target.value, number))}
            className={cn(
              'h-10 w-[6.5rem] shrink-0 rounded-input border border-neutral-300 bg-white px-2 text-body text-neutral-900',
              'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 max-md:h-11',
            )}
          >
            {DIAL_CODES.map((d) => (
              <option key={d.code + d.country} value={d.code}>+{d.code} {d.country}</option>
            ))}
            {!DIAL_CODES.some((d) => d.code === code) && <option value={code}>+{code}</option>}
          </select>
          <input
            {...p}
            type="tel"
            inputMode="tel"
            autoComplete="tel-national"
            value={number}
            disabled={disabled}
            placeholder={placeholder ?? (code === '91' ? '98765 43210' : 'Phone number')}
            onChange={(e) => onChange(joinPhone(code, e.target.value.replace(/\D/g, '').slice(0, 14)))}
            className={cn(
              'h-10 min-w-0 flex-1 rounded-input border border-neutral-300 bg-white px-3 font-mono text-body text-neutral-900',
              'focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 max-md:h-11',
              error && 'border-danger-500',
            )}
          />
        </div>
      )}
    </Field>
  );
}
